import type { ReactNode } from "react";
import { Lettering } from "../ui/primitives.js";

/**
 * THE MARKER (2026-10-31, Andre, from the arrange-files ideation, chunks 10
 * and 11): one small printed sign at each end of a connection, the same sign
 * on both, so the eye can pair them before any line is drawn: a ring at the
 * end of the recommendation paragraph in the conversation, and the same ring
 * on the file's own reason line. A button, so it is reached by mouse, by
 * keyboard and by touch alike; `aria-pressed` says which pair is open. The
 * word after the ring is the marker's reading when the lines are hidden
 * ("why this case?" on the file; "on your desk." in the conversation), so
 * the relationship is legible from either end without the drawing.
 *
 * `data-link-end` names the element for the overlay, which measures it to
 * draw the line; `onPointerDown` stops here so a press on a file's marker
 * never begins a drag of the file, as the doors do.
 */
export function LinkMark({
  end,
  word,
  hint,
  selected,
  tone = "plain",
  onSelect,
}: {
  /** The overlay's name for this end: `thread:<messageId>`, `card:<messageId>`. */
  readonly end: string;
  readonly word: string;
  readonly hint: string;
  readonly selected: boolean;
  /** `focus`: this recommendation was made for the chosen focus; the ring is filled. */
  readonly tone?: "plain" | "focus";
  readonly onSelect: () => void;
}): ReactNode {
  return (
    <button
      type="button"
      className="pcs-link-mark"
      data-link-end={end}
      data-tone={tone}
      aria-pressed={selected}
      title={hint}
      onPointerDown={(e) => { e.stopPropagation(); }}
      onClick={(e) => { e.stopPropagation(); onSelect(); }}
    >
      <svg className="pcs-link-ring" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.4" aria-hidden="true" focusable="false">
        <circle cx="8" cy="8" r="5" />
        {tone === "focus" || selected ? <circle cx="8" cy="8" r="2.2" fill="currentColor" stroke="none" /> : null}
      </svg>
      <Lettering className="pcs-link-word">{word}</Lettering>
    </button>
  );
}
